import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from './components/Layout/Layout';
import Dashboard from './pages/Dashboard/Dashboard';
import Login from './pages/Login/Login';
import TenderList from './pages/Tenders/TenderList';
import TenderForm from './pages/Tenders/TenderForm';
import AnnouncementList from './pages/Announcements/AnnouncementList';
import AnnouncementForm from './pages/Announcements/AnnouncementForm';
import EventList from './pages/Events/EventList';
import EventForm from './pages/Events/EventForm';
import Parks from './pages/Parks/Parks';
import ParlamentMembers from './pages/ParlamentMembers/ParlamentMembers';
import NewsList from './pages/News/NewsList';
import PressCoverage from './pages/PressCoverage/PressCoverage';
import NewsForm from './pages/News/NewsForm';
import Settings from './pages/Settings/Settings';
import ManagerList from './pages/Managers/ManagerList';
import ManagerForm from './pages/Managers/ManagerForm';
import OrganizationManagement from './pages/Organization/OrganizationManagement';
import ActivityReportList from './pages/ActivityReports/ActivityReportList';
import MenuList from './pages/Menu/MenuList';
import AdvertisementList from './pages/Advertisements/AdvertisementList';
import PageList from './pages/Pages/PageList';
import PageForm from './pages/Pages/PageForm';
import SliderList from './pages/Sliders/SliderList';
import ProjectList from './pages/Projects/ProjectList';
import ProjectForm from './pages/Projects/ProjectForm';
import ParlamentDecisionList from './pages/ParlamentDecisions/ParlamentDecisionList';
import ParlamentDecisionForm from './pages/ParlamentDecisions/ParlamentDecisionForm';
import LibraryAppointments from './pages/LibraryAppointments/LibraryAppointments';
import LibraryBlockedSlots from './pages/LibraryAppointments/LibraryBlockedSlots';
import LibraryQrScan from './pages/LibraryAppointments/LibraryQrScan';
import SportsAppointments from './pages/SportsAppointments/SportsAppointments';
import ClubApplications from './pages/ClubApplications/ClubApplications';
import ClubPaymentPage from './pages/ClubApplications/ClubPaymentPage';
import RegionsManagement from './pages/SportsManagement/RegionsManagement';
import FacilitiesManagement from './pages/SportsManagement/FacilitiesManagement';
import PricingManagement from './pages/SportsManagement/PricingManagement';
import BlockedSlotsManagement from './pages/SportsManagement/BlockedSlotsManagement';
import RegionalUsersManagement from './pages/SportsManagement/RegionalUsersManagement';
import ActivityLogs from './pages/SportsManagement/ActivityLogs';
import BookManagement from './pages/Books/BookManagement';
import PrinterManagement from './pages/Printers/PrinterManagement';
import NeighborhoodList from './pages/NeighborhoodDevelopments/NeighborhoodList';
import NeighborhoodForm from './pages/NeighborhoodDevelopments/NeighborhoodForm';
import ReportIncidents from './pages/ReportIncidents/ReportIncidents';
import MobileNotifications from './pages/MobileNotifications/MobileNotifications';
import SitasDashboard from './pages/Sitas/SitasDashboard';
import { getToken } from './services/authService';
import api from './services/api';

import './styles/main.scss';
import './styles/responsive.scss';
import './styles/arc-theme.scss';

const PrivateRoute = ({ children, allowedRoles }) => {
  const token = getToken();
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  const user = (() => {
    const stored = localStorage.getItem('user');
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  })();
  const role = (user?.userType || user?.role || 'admin').toLowerCase();
  const isSportsPanelRole = ['sports', 'sporsuperadmin', 'regionalsports', 'istirakler'].includes(role);

  if (!token || !isAuthenticated) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.map((r) => r.toLowerCase()).includes(role)) {
    // Rol bazlı yönlendirme
    if (role === 'library') {
      return <Navigate to="/library-appointments" />;
    } else if (role === 'printer') {
      return <Navigate to="/printers" />;
    } else if (isSportsPanelRole) {
      return <Navigate to="/sports-appointments" />;
    } else if (role === 'department') {
      return <Navigate to="/report-incidents" />;
    } else if (role === 'basin') {
      return <Navigate to="/projects" />;
    } else if (role === 'muhtar') {
      return <Navigate to="/neighborhood-developments" />;
    } else {
      return <Navigate to="/dashboard" />;
    }
  }

  return <Layout>{children}</Layout>;
};

const App = () => {
  useEffect(() => {
    const token = getToken();
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';

    if (!token || !isAuthenticated) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('isAuthenticated');
      return undefined;
    }

    let validationInProgress = false;

    const validateSession = async () => {
      if (validationInProgress || !getToken()) return;

      validationInProgress = true;
      try {
        await api.get('/api/Authentication/ValidateSession');
      } catch {
        // The shared API interceptor clears storage and redirects on 401.
      } finally {
        validationInProgress = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') validateSession();
    };

    const handleStorageChange = (event) => {
      if (event.key === 'token' && !event.newValue) {
        window.location.href = '/login';
      }
    };

    validateSession();
    const intervalId = window.setInterval(validateSession, 30000);
    window.addEventListener('focus', validateSession);
    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', validateSession);
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Rol bazlı ana sayfa yönlendirmesi
  const getDefaultRoute = () => {
    const user = (() => {
      const stored = localStorage.getItem('user');
      if (!stored) return null;
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    })();
    const role = (user?.userType || user?.role || 'admin').toLowerCase();

    if (role === 'library') {
      return '/library-appointments';
    } else if (role === 'printer') {
      return '/printers';
    } else if (['sports', 'sporsuperadmin', 'regionalsports', 'istirakler'].includes(role)) {
      return '/sports-appointments';
    } else if (role === 'department') {
      return '/report-incidents';
    } else if (role === 'basin') {
      return '/projects';
    } else if (role === 'muhtar') {
      return '/neighborhood-developments';
    } else {
      return '/dashboard';
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="colored" />
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Navigate to={getDefaultRoute()} />} />

          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <PrivateRoute allowedRoles={['admin']}>
              <Dashboard />
            </PrivateRoute>
          } />

          {/* Tender Routes */}
          <Route path="/tenders" element={
            <PrivateRoute allowedRoles={['admin']}>
              <TenderList />
            </PrivateRoute>
          } />
          <Route path="/tenders/add" element={
            <PrivateRoute allowedRoles={['admin']}>
              <TenderForm />
            </PrivateRoute>
          } />
          <Route path="/tenders/edit/:id" element={
            <PrivateRoute allowedRoles={['admin']}>
              <TenderForm />
            </PrivateRoute>
          } />

          {/* Announcement Routes */}
          <Route path="/announcements" element={
            <PrivateRoute allowedRoles={['admin', 'basin']}>
              <AnnouncementList />
            </PrivateRoute>
          } />
          <Route path="/announcements/add" element={
            <PrivateRoute allowedRoles={['admin', 'basin']}>
              <AnnouncementForm />
            </PrivateRoute>
          } />
          <Route path="/announcements/edit/:id" element={
            <PrivateRoute allowedRoles={['admin', 'basin']}>
              <AnnouncementForm />
            </PrivateRoute>
          } />

          {/* Parliament Members Routes */}
          <Route path="/parlament-members" element={
            <PrivateRoute allowedRoles={['admin']}>
              <ParlamentMembers />
            </PrivateRoute>
          } />

          {/* Parks Routes */}
          <Route path="/parks" element={
            <PrivateRoute allowedRoles={['admin']}>
              <Parks />
            </PrivateRoute>
          } />

          {/* Event Routes */}
          <Route path="/events" element={
            <PrivateRoute allowedRoles={['admin', 'basin']}>
              <EventList />
            </PrivateRoute>
          } />
          <Route path="/events/add" element={
            <PrivateRoute allowedRoles={['admin', 'basin']}>
              <EventForm />
            </PrivateRoute>
          } />

          {/* News Routes */}
          <Route path="/news" element={
            <PrivateRoute allowedRoles={['admin', 'basin']}>
              <NewsList />
            </PrivateRoute>
          } />
          <Route path="/press-coverage" element={<PrivateRoute allowedRoles={['admin', 'basin']}><PressCoverage /></PrivateRoute>} />
          <Route path="/news/add" element={
            <PrivateRoute allowedRoles={['admin', 'basin']}>
              <NewsForm />
            </PrivateRoute>
          } />

          {/* Manager Routes */}
          <Route path="/managers" element={
            <PrivateRoute allowedRoles={['admin']}>
              <ManagerList />
            </PrivateRoute>
          } />
          <Route path="/managers/add" element={
            <PrivateRoute allowedRoles={['admin']}>
              <ManagerForm />
            </PrivateRoute>
          } />
          <Route path="/organization" element={
            process.env.NODE_ENV === 'development' ? (
              <PrivateRoute allowedRoles={['admin']}>
                <OrganizationManagement />
              </PrivateRoute>
            ) : (
              <Navigate to="/" replace />
            )
          } />

          {/* Activity Report Routes */}
          <Route path="/activity-reports" element={<PrivateRoute allowedRoles={['admin']}><ActivityReportList /></PrivateRoute>} />

          {/* Menu Routes */}
          <Route path="/menu" element={
            <PrivateRoute>
              <MenuList />
            </PrivateRoute>
          } />

          {/* Advertisement Routes */}
          <Route path="/advertisements" element={
            <PrivateRoute>
              <AdvertisementList />
            </PrivateRoute>
          } />

          {/* Page Routes */}
          <Route path="/pages" element={
            <PrivateRoute>
              <PageList />
            </PrivateRoute>
          } />
          <Route path="/pages/add" element={
            <PrivateRoute>
              <PageForm />
            </PrivateRoute>
          } />
          <Route path="/pages/edit/:id" element={
            <PrivateRoute>
              <PageForm />
            </PrivateRoute>
          } />

          {/* Slider Routes */}
          <Route path="/sliders" element={
            <PrivateRoute>
              <SliderList />
            </PrivateRoute>
          } />

          {/* Sports Management Routes */}
          <Route path="/sports/regions" element={<PrivateRoute allowedRoles={['admin', 'sports', 'sporsuperadmin', 'regionalsports']}><RegionsManagement /></PrivateRoute>} />
          <Route path="/sports/facilities" element={<PrivateRoute allowedRoles={['admin', 'sports', 'sporsuperadmin', 'regionalsports']}><FacilitiesManagement /></PrivateRoute>} />
          <Route path="/sports/pricing" element={<PrivateRoute allowedRoles={['admin', 'sports', 'sporsuperadmin', 'regionalsports']}><PricingManagement /></PrivateRoute>} />
          <Route path="/sports/blocked-slots" element={<PrivateRoute allowedRoles={['admin', 'sports', 'sporsuperadmin', 'regionalsports']}><BlockedSlotsManagement /></PrivateRoute>} />
          <Route path="/sports/users" element={<PrivateRoute allowedRoles={['admin', 'sports', 'sporsuperadmin']}><RegionalUsersManagement /></PrivateRoute>} />
          <Route path="/sports/activity-logs" element={<PrivateRoute allowedRoles={['admin', 'sports', 'sporsuperadmin', 'istirakler']}><ActivityLogs /></PrivateRoute>} />

          {/* Project Routes */}
          <Route path="/projects" element={
            <PrivateRoute allowedRoles={['admin', 'basin']}>
              <ProjectList />
            </PrivateRoute>
          } />
          <Route path="/projects/add" element={
            <PrivateRoute allowedRoles={['admin', 'basin']}>
              <ProjectForm />
            </PrivateRoute>
          } />
          <Route path="/projects/edit/:id" element={
            <PrivateRoute allowedRoles={['admin', 'basin']}>
              <ProjectForm />
            </PrivateRoute>
          } />

          {/* Neighborhood Developments Routes */}
          <Route path="/neighborhood-developments" element={
            <PrivateRoute allowedRoles={['admin', 'basin', 'muhtar', 'department']}>
              <NeighborhoodList />
            </PrivateRoute>
          } />
          <Route path="/neighborhood-developments/add" element={
            <PrivateRoute allowedRoles={['admin', 'basin', 'muhtar']}>
              <NeighborhoodForm />
            </PrivateRoute>
          } />
          <Route path="/neighborhood-developments/edit/:id" element={
            <PrivateRoute allowedRoles={['admin', 'basin', 'muhtar']}>
              <NeighborhoodForm />
            </PrivateRoute>
          } />

          {/* Parliament Decisions Routes */}
          <Route path="/parlament-decisions" element={
            <PrivateRoute>
              <ParlamentDecisionList />
            </PrivateRoute>
          } />
          <Route path="/parlament-decisions/add" element={
            <PrivateRoute>
              <ParlamentDecisionForm />
            </PrivateRoute>
          } />
          <Route path="/parlament-decisions/edit/:id" element={
            <PrivateRoute>
              <ParlamentDecisionForm />
            </PrivateRoute>
          } />

          {/* Settings Route */}
          <Route path="/settings" element={
            <PrivateRoute allowedRoles={['admin']}>
              <Settings />
            </PrivateRoute>
          } />
          <Route path="/printers" element={
            <PrivateRoute allowedRoles={['admin', 'printer']}>
              <PrinterManagement />
            </PrivateRoute>
          } />
          <Route path="/library-appointments" element={
            <PrivateRoute allowedRoles={['admin', 'library']}>
              <LibraryAppointments />
            </PrivateRoute>
          } />
          <Route path="/library-appointments/scan" element={
            <PrivateRoute allowedRoles={['admin', 'library']}>
              <LibraryQrScan />
            </PrivateRoute>
          } />
          <Route path="/books" element={
            <PrivateRoute allowedRoles={['admin', 'library']}>
              <BookManagement />
            </PrivateRoute>
          } />
          <Route path="/library-appointments/blocked-slots" element={
            <PrivateRoute allowedRoles={['admin', 'library']}>
              <LibraryBlockedSlots />
            </PrivateRoute>
          } />
          <Route path="/sports-appointments" element={
            <PrivateRoute allowedRoles={['admin', 'sports', 'sporsuperadmin', 'regionalsports', 'istirakler']}>
              <SportsAppointments />
            </PrivateRoute>
          } />
          <Route path="/report-incidents" element={
            <PrivateRoute allowedRoles={['admin', 'department']}>
              <ReportIncidents />
            </PrivateRoute>
          } />
          <Route path="/sitas" element={
            <PrivateRoute allowedRoles={['admin']}>
              <SitasDashboard />
            </PrivateRoute>
          } />
          <Route path="/mobile-notifications" element={
            process.env.NODE_ENV === 'development' ? (
              <PrivateRoute allowedRoles={['admin']}>
                <MobileNotifications />
              </PrivateRoute>
            ) : (
              <Navigate to="/" replace />
            )
          } />
        </Routes>
      </Router>
    </>
  );
};

export default App;
